import {
  HHButton,
  HHStack,
  HHTypography,
} from '@hinge-health/react-component-library';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  Box,
  Collapse,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from '@mui/material';
import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useHingeSelectClient } from '../../api';
import { CodeSet } from '../../api/types';
import {
  computeCounts,
  DedupeSummary,
} from '../components/badges/dedupe-summary';
import { StatusBadge } from '../components/badges/status-badge';
import { DisabledInfo } from '../components/layout/step-nav';
import { ContractInput, ResolveResult, ResolveStatus } from '../context/types';
import { useWizardState } from '../context/wizard-state.context';
import { runConcurrent } from '../utils/concurrency';
import { CodeGroup, expandCodeRows, flattenCodes } from '../utils/expand-codes';

export interface SectionReadiness {
  ready: boolean;
  disabledInfo?: DisabledInfo;
}

const RESOLVE_CONCURRENCY = 8;

const monoCell = {
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
  fontVariantNumeric: 'tabular-nums' as const,
  fontSize: 13,
};

interface PerCodeView {
  status: ResolveStatus;
  itemId?: string;
  errorMessage?: string;
}

interface CodeTableProps {
  title: string;
  groups: CodeGroup[];
  resolutions: Record<string, PerCodeView>;
  onRetry?: (code: string) => void;
  muted?: boolean;
  excluded?: boolean;
}

const RetryCell = ({
  code,
  onRetry,
}: {
  code: string;
  onRetry: (code: string) => void;
}): JSX.Element => (
  <Tooltip title="Retry resolution">
    <IconButton
      size="small"
      onClick={(): void => onRetry(code)}
      sx={{ p: 0.25 }}
    >
      <RefreshIcon sx={{ fontSize: 16 }} />
    </IconButton>
  </Tooltip>
);

const CodeTable = ({
  title,
  groups,
  resolutions,
  onRetry,
  muted,
  excluded,
}: CodeTableProps): JSX.Element => {
  const [open, setOpen] = useState(false);
  const totalCodes = groups.reduce((s, g) => s + g.codes.length, 0);
  const matchingCount = groups
    .flatMap(g => g.codes)
    .filter(c => resolutions[c]?.status === 'existing').length;

  const collapsedSummary = excluded
    ? `${totalCodes} code${totalCodes === 1 ? '' : 's'} — excluded`
    : `${totalCodes} code${totalCodes === 1 ? '' : 's'} · ${matchingCount} matching`;

  return (
    <HHStack spacing={1}>
      <Box
        role="button"
        tabIndex={0}
        onClick={(): void => setOpen(o => !o)}
        onKeyDown={(e): void => {
          if (e.key === 'Enter' || e.key === ' ') setOpen(o => !o);
        }}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          px: 1.5,
          py: 1,
          borderRadius: 1,
          border: '1px solid',
          borderColor: 'divider',
          backgroundColor: 'rgba(0,0,0,0.03)',
          cursor: 'pointer',
          userSelect: 'none',
          '&:hover': { backgroundColor: 'rgba(0,0,0,0.06)' },
          '&:focus-visible': {
            outline: '2px solid',
            outlineColor: 'primary.main',
            outlineOffset: 2,
          },
        }}
      >
        <HHTypography hhVariant="subtitle1" sx={{ fontWeight: 600 }}>
          {title}
        </HHTypography>
        <HHTypography
          hhVariant="caption"
          sx={{ opacity: 0.7, fontVariantNumeric: 'tabular-nums' }}
        >
          {open
            ? `${groups.length} input${groups.length === 1 ? '' : 's'} · ${totalCodes} code${totalCodes === 1 ? '' : 's'}`
            : collapsedSummary}
        </HHTypography>
        <Box sx={{ flexGrow: 1 }} />
        {open ? (
          <ExpandLessIcon sx={{ fontSize: 20, opacity: 0.6 }} />
        ) : (
          <ExpandMoreIcon sx={{ fontSize: 20, opacity: 0.6 }} />
        )}
      </Box>
      <Collapse in={open}>
        <TableContainer
          component={Paper}
          variant="outlined"
          sx={{
            opacity: muted ? 0.6 : 1,
            maxHeight: 480,
          }}
        >
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 140 }}>CPT</TableCell>
                <TableCell>Description</TableCell>
                <TableCell sx={{ width: 140 }}>Status</TableCell>
                <TableCell sx={{ width: 220 }}>Item ID</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {groups.map(g => {
                if (excluded) {
                  return (
                    <TableRow key={g.source}>
                      <TableCell sx={monoCell}>{g.source}</TableCell>
                      <TableCell>{g.description}</TableCell>
                      <TableCell colSpan={2}>
                        <HHTypography hhVariant="caption" sx={{ opacity: 0.7 }}>
                          Excluded — not added to fee schedule
                        </HHTypography>
                      </TableCell>
                    </TableRow>
                  );
                }
                if (g.isExpansion) {
                  return (
                    <Fragment key={g.source}>
                      <TableRow sx={{ backgroundColor: 'rgba(0,0,0,0.02)' }}>
                        <TableCell sx={{ ...monoCell, fontWeight: 600 }}>
                          {g.source}
                        </TableCell>
                        <TableCell>
                          <HHTypography hhVariant="body2">
                            {g.description}{' '}
                            <span style={{ opacity: 0.6 }}>
                              (expanded to {g.codes.length})
                            </span>
                          </HHTypography>
                        </TableCell>
                        <TableCell colSpan={2} />
                      </TableRow>
                      {g.codes.map(c => {
                        const r = resolutions[c] ?? {
                          status: 'pending' as const,
                        };
                        return (
                          <TableRow key={c}>
                            <TableCell sx={{ ...monoCell, pl: 4 }}>
                              {c}
                            </TableCell>
                            <TableCell>
                              <HHTypography
                                hhVariant="body2"
                                sx={{ opacity: 0.65 }}
                              >
                                {g.description}
                              </HHTypography>
                              {r.status === 'error' && r.errorMessage && (
                                <HHTypography
                                  hhVariant="caption"
                                  sx={{ color: '#fd7e14', display: 'block' }}
                                >
                                  {r.errorMessage}
                                </HHTypography>
                              )}
                            </TableCell>
                            <TableCell>
                              <HHStack
                                direction="row"
                                spacing={0.5}
                                alignItems="center"
                              >
                                <StatusBadge status={r.status} />
                                {r.status === 'error' && onRetry && (
                                  <RetryCell code={c} onRetry={onRetry} />
                                )}
                              </HHStack>
                            </TableCell>
                            <TableCell sx={monoCell}>
                              {r.itemId ?? '—'}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </Fragment>
                  );
                }
                const c = g.codes[0];
                const r = resolutions[c] ?? { status: 'pending' as const };
                return (
                  <TableRow key={c}>
                    <TableCell sx={monoCell}>{c}</TableCell>
                    <TableCell>
                      {g.description}
                      {r.status === 'error' && r.errorMessage && (
                        <HHTypography
                          hhVariant="caption"
                          sx={{ color: '#fd7e14', display: 'block' }}
                        >
                          {r.errorMessage}
                        </HHTypography>
                      )}
                    </TableCell>
                    <TableCell>
                      <HHStack
                        direction="row"
                        spacing={0.5}
                        alignItems="center"
                      >
                        <StatusBadge status={r.status} />
                        {r.status === 'error' && onRetry && (
                          <RetryCell code={c} onRetry={onRetry} />
                        )}
                      </HHStack>
                    </TableCell>
                    <TableCell sx={monoCell}>{r.itemId ?? '—'}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Collapse>
    </HHStack>
  );
};

interface CodesSectionProps {
  codes: ContractInput['fee_schedule']['codes'];
  onReady: (readiness: SectionReadiness) => void;
}

export const CodesSection = ({
  codes,
  onReady,
}: CodesSectionProps): JSX.Element => {
  const { state, setState } = useWizardState();
  const client = useHingeSelectClient();
  const fired = useRef(false);

  const groups = useMemo(
    () => ({
      categoryA: expandCodeRows(codes.category_a),
      categoryB: expandCodeRows(codes.category_b),
      categoryC: expandCodeRows(codes.category_c_excluded),
    }),
    [codes],
  );

  const allResolvableCodes = useMemo(
    () => flattenCodes([...groups.categoryA, ...groups.categoryB]),
    [groups.categoryA, groups.categoryB],
  );

  // Project the wizard's resolutions.items into a per-CPT view: each expanded
  // CPT renders pending until its resolve settles, then existing/new/conflict/error.
  const perCodeResolutions: Record<string, PerCodeView> = useMemo(() => {
    const out: Record<string, PerCodeView> = {};
    for (const c of allResolvableCodes) {
      const r = state.resolutions.items[c];
      if (!r) {
        out[c] = { status: 'pending' };
        continue;
      }
      out[c] = {
        status: r.status,
        itemId: r.data?.itemId,
        errorMessage: r.errorMessage,
      };
    }
    return out;
  }, [allResolvableCodes, state.resolutions.items]);

  const resolveOne = useCallback(
    async (code: string): Promise<void> => {
      try {
        const result = await client.resolveProcedureCodeToItem({
          codeset: CodeSet.HCPCS,
          procedureCode: code,
        });
        let projected: ResolveResult<{ itemId: string }>;
        if (result.ok) {
          projected = {
            status: 'existing',
            data: { itemId: result.data.item.id ?? '' },
          };
        } else if (result.reason === 'Conflict') {
          // Server reports a real conflict: an existing record exists but
          // mismatches the input. Block continue; operator must resolve.
          projected = { status: 'conflict', reason: result.reason };
        } else {
          // NotFound or Terminated: code is absent from the KG — shown as "new"
          // for operator visibility only. No entities are created during commit.
          projected = { status: 'new', reason: result.reason };
        }
        setState(s => ({
          ...s,
          resolutions: {
            ...s.resolutions,
            items: { ...s.resolutions.items, [code]: projected },
          },
        }));
      } catch (e) {
        const errorMessage = (e as Error).message;
        setState(s => ({
          ...s,
          resolutions: {
            ...s.resolutions,
            items: {
              ...s.resolutions.items,
              [code]: { status: 'error', errorMessage },
            },
          },
        }));
      }
    },
    [client, setState],
  );

  const handleRetry = useCallback(
    (code: string): void => {
      // Mark pending immediately so the badge re-renders to a skeleton.
      setState(s => {
        const next = { ...s.resolutions.items };
        delete next[code];
        return { ...s, resolutions: { ...s.resolutions, items: next } };
      });
      void resolveOne(code);
    },
    [resolveOne, setState],
  );

  const handleRetryAllErrors = useCallback((): void => {
    const errored = allResolvableCodes.filter(
      c => state.resolutions.items[c]?.status === 'error',
    );
    setState(s => {
      const next = { ...s.resolutions.items };
      for (const c of errored) delete next[c];
      return { ...s, resolutions: { ...s.resolutions, items: next } };
    });
    const tasks = errored.map(c => () => resolveOne(c));
    void runConcurrent(tasks, RESOLVE_CONCURRENCY);
  }, [allResolvableCodes, resolveOne, setState, state.resolutions.items]);

  // Fire all unresolved CPTs in parallel chunks once when entering the step.
  // Cached results across back-nav: skip codes already in state.resolutions.items.
  useEffect(() => {
    if (fired.current) return;
    const unresolved = allResolvableCodes.filter(
      c => state.resolutions.items[c] == null,
    );
    if (unresolved.length === 0) return;
    fired.current = true;
    const tasks = unresolved.map(c => () => resolveOne(c));
    void runConcurrent(tasks, RESOLVE_CONCURRENCY);
  }, [allResolvableCodes, resolveOne, state.resolutions.items]);

  const counts = useMemo(
    () =>
      computeCounts(allResolvableCodes.map(c => perCodeResolutions[c].status)),
    [allResolvableCodes, perCodeResolutions],
  );

  const canContinue =
    counts.pending === 0 && counts.conflict === 0 && counts.error === 0;
  const trailing =
    counts.error > 0
      ? `${counts.error} code${
          counts.error === 1 ? '' : 's'
        } failed to resolve — retry or check the BFF connection`
      : counts.conflict > 0
        ? `${counts.conflict} conflict${
            counts.conflict === 1 ? '' : 's'
          } must be resolved before continuing`
        : counts.pending > 0
          ? 'Waiting for resolutions to settle…'
          : undefined;
  // Push readiness up to the parent step (fee-schedule). Stable primitives
  // in deps so the effect only fires when the relevant counts change.
  useEffect(() => {
    if (canContinue) {
      onReady({ ready: true });
      return;
    }
    if (counts.error > 0) {
      onReady({
        ready: false,
        disabledInfo: {
          message: `${counts.error} code${counts.error === 1 ? '' : 's'} failed to resolve. Retry the failed codes or check the BFF connection.`,
          severity: 'error',
        },
      });
      return;
    }
    if (counts.conflict > 0) {
      onReady({
        ready: false,
        disabledInfo: {
          message: `${counts.conflict} conflict${counts.conflict === 1 ? '' : 's'} must be resolved before continuing.`,
          severity: 'warning',
        },
      });
      return;
    }
    if (counts.pending > 0) {
      onReady({
        ready: false,
        disabledInfo: {
          message: 'Waiting for code resolutions to settle…',
          severity: 'info',
        },
      });
      return;
    }
    onReady({ ready: false });
  }, [canContinue, counts.error, counts.conflict, counts.pending, onReady]);

  return (
    <HHStack spacing={3}>
      <HHStack spacing={0.5}>
        <HHTypography hhVariant="subtitle1">Procedure codes</HHTypography>
        <HHTypography hhVariant="caption" sx={{ opacity: 0.7 }}>
          Each CPT is resolved against the Knowledge Graph. Cat B ranges are
          auto-expanded. Cat C codes are excluded from the fee schedule.
        </HHTypography>
      </HHStack>

      <DedupeSummary counts={counts} trailing={trailing} />

      {counts.error > 0 && (
        <HHStack direction="row" spacing={2}>
          <HHButton
            hhVariant="variant-bypass"
            variant="outlined"
            color="warning"
            onClick={handleRetryAllErrors}
          >
            Retry all {counts.error} failed{' '}
            {counts.error === 1 ? 'code' : 'codes'}
          </HHButton>
        </HHStack>
      )}

      <CodeTable
        title="Category A — anchors"
        groups={groups.categoryA}
        resolutions={perCodeResolutions}
        onRetry={handleRetry}
      />
      <CodeTable
        title="Category B — supporting"
        groups={groups.categoryB}
        resolutions={perCodeResolutions}
        onRetry={handleRetry}
      />
      <CodeTable
        title="Category C — excluded"
        groups={groups.categoryC}
        resolutions={{}}
        muted
        excluded
      />
    </HHStack>
  );
};
